import { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import { toLocalDateTime } from '../../utils/date-helper';
import {
  useAddSupportTicketComment,
  useCloseSupportTicket,
  useReopenSupportTicket,
  useSupportTicketDetail,
} from '../../hooks/useSupportTickets';
import { isClosedStatus, prioritySeverity, statusSeverity } from './supportTicketHelpers';
import Markdown from './Markdown';

const TicketDetailDialog = (props: { issueId?: string; onHide: () => void }): JSX.Element => {
  const { issueId, onHide } = props;

  const { data: ticket, isPending, isError, error } = useSupportTicketDetail(issueId);
  const addCommentMutation = useAddSupportTicketComment(issueId ?? '');
  const closeMutation = useCloseSupportTicket();
  const reopenMutation = useReopenSupportTicket();

  const [reply, setReply] = useState('');

  const actionErrorObject =
    addCommentMutation.error ?? closeMutation.error ?? reopenMutation.error;
  const actionError = actionErrorObject
    ? actionErrorObject instanceof Error
      ? actionErrorObject.message
      : 'Action failed. Please try again.'
    : null;

  const submitReply = async () => {
    if (!issueId || !reply.trim()) return;
    closeMutation.reset();
    reopenMutation.reset();
    try {
      await addCommentMutation.mutateAsync({ body: reply.trim(), attachments: [] });
      setReply('');
    } catch {
      // Error surfaced via addCommentMutation.error.
    }
  };

  const runClose = () => {
    if (!issueId) return;
    confirmDialog({
      group: 'ticket-detail',
      message: 'Close this ticket? You can reopen it later if needed.',
      header: 'Close Ticket',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        addCommentMutation.reset();
        reopenMutation.reset();
        try {
          await closeMutation.mutateAsync(issueId);
        } catch {
          // Error surfaced via closeMutation.error.
        }
      },
    });
  };

  const runReopen = () => {
    if (!issueId) return;
    confirmDialog({
      group: 'ticket-detail',
      message: 'Reopen this ticket?',
      header: 'Reopen Ticket',
      icon: 'pi pi-replay',
      accept: async () => {
        addCommentMutation.reset();
        closeMutation.reset();
        try {
          await reopenMutation.mutateAsync(issueId);
        } catch {
          // Error surfaced via reopenMutation.error.
        }
      },
    });
  };

  const lastComment = ticket?.comments[ticket.comments.length - 1];
  const waitingLabel = lastComment
    ? lastComment.isFromCustomer
      ? 'Waiting on Mezzo'
      : 'Waiting on you'
    : null;

  const closed = ticket ? isClosedStatus(ticket.status) : false;

  const header = ticket ? `${ticket.identifier} — ${ticket.title}` : 'Support Ticket';

  return (
    <Dialog
      header={header}
      visible={!!issueId}
      onHide={onHide}
      style={{ width: '48rem' }}
      breakpoints={{ '960px': '95vw' }}
    >
      <ConfirmDialog group="ticket-detail" />
      {isPending && (
        <div className="flex justify-center py-8">
          <ProgressSpinner style={{ width: '2.5rem', height: '2.5rem' }} />
        </div>
      )}

      {isError && (
        <div className="text-red-600 text-sm py-4">
          {error instanceof Error ? error.message : 'Unable to load this ticket.'}
        </div>
      )}

      {ticket && (
        <div className="flex flex-col gap-5 py-6">
          <div className="flex flex-wrap items-center gap-2">
            <Tag value={ticket.status} severity={statusSeverity(ticket.status)} />
            <Tag value={ticket.priority} severity={prioritySeverity(ticket.priority)} />
            {ticket.assigneeName && (
              <span className="text-sm text-gray-500">Assigned to {ticket.assigneeName}</span>
            )}
            {waitingLabel && (
              <span className="text-xs font-medium text-[#2571ab] ml-auto">{waitingLabel}</span>
            )}
          </div>

          {ticket.description && <Markdown content={ticket.description} />}

          {ticket.attachments.length > 0 && (
            <div className="flex flex-col gap-1">
              <div className="text-sm font-medium text-gray-700">Attachments</div>
              <div className="flex flex-col gap-1">
                {ticket.attachments.map((attachment) => (
                  <a
                    key={attachment.url}
                    href={attachment.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-[#2571ab] hover:underline"
                  >
                    <i className="pi pi-paperclip mr-1" />
                    {attachment.title || 'Attachment'}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <div className="text-sm font-medium text-gray-700">Conversation</div>
            {ticket.comments.length === 0 && (
              <div className="text-sm text-gray-400">No replies yet.</div>
            )}
            {ticket.comments.map((comment) => (
              <div
                key={comment.id}
                className={classNames('rounded-lg border p-3', {
                  'bg-blue-50 border-blue-100': !comment.isFromCustomer,
                  'bg-gray-50 border-gray-100': comment.isFromCustomer,
                })}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-800">{comment.authorName}</span>
                  {!comment.isFromCustomer && (
                    <Tag value="Mezzo" severity="info" className="text-[10px]" />
                  )}
                  <span className="text-xs text-gray-400 ml-auto">
                    {toLocalDateTime(comment.createdAt)}
                  </span>
                </div>
                <Markdown content={comment.body} />
              </div>
            ))}
          </div>

          {actionError && <div className="text-red-600 text-sm">{actionError}</div>}

          {!closed && (
            <div className="flex flex-col gap-2">
              <InputTextarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={3}
                autoResize
                placeholder="Write a reply to Mezzo support"
              />
              <div className="flex items-center justify-between">
                <Button
                  label="Close Ticket"
                  icon="pi pi-times"
                  severity="secondary"
                  outlined
                  onClick={runClose}
                  loading={closeMutation.isPending}
                />
                <Button
                  label="Send Reply"
                  icon="pi pi-send"
                  onClick={submitReply}
                  loading={addCommentMutation.isPending}
                  disabled={!reply.trim()}
                />
              </div>
            </div>
          )}

          {closed && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">This ticket is closed.</span>
              <Button
                label="Reopen Ticket"
                icon="pi pi-replay"
                outlined
                onClick={runReopen}
                loading={reopenMutation.isPending}
              />
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};

export default TicketDetailDialog;
